// The crew: blocky sailors who actually live in the ship — they wander
// between the mess deck, their racks and the weather deck, run to battle
// stations at GQ, and the repair parties physically run to whatever's
// burning. Pathfinding is A* straight over the walkable voxel cells (the
// same cells the player walks), with ladder cells linking decks.

import * as THREE from "three";

const WALK_SPEED = 1.5, RUN_SPEED = 3.4, CLIMB_SPEED = 1.6;

// ---------------------------------------------------------------------------
// path graph over the voxel grid
class Nav {
  constructor(grid) { this.g = grid; }
  walkable(x, y, z) {
    const g = this.g;
    if (g.climbable(x, y, z)) return true;
    return g.solid(x, y - 1, z) && !g.solid(x, y, z) && !g.solid(x, y + 1, z);
  }
  key(x, y, z) { return (x << 14) | (y << 8) | z; }
  neighbors(x, y, z, out) {
    out.length = 0;
    const g = this.g;
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx, nz = z + dz;
      if (this.walkable(nx, y, nz)) out.push([nx, y, nz]);
      else if (this.walkable(nx, y + 1, nz) && !g.solid(x, y + 2, z)) out.push([nx, y + 1, nz]); // step up
      else if (this.walkable(nx, y - 1, nz)) out.push([nx, y - 1, nz]); // step down
    }
    if (g.climbable(x, y, z)) {
      if (this.walkable(x, y + 1, z)) out.push([x, y + 1, z]);
      if (this.walkable(x, y - 1, z)) out.push([x, y - 1, z]);
    } else if (g.climbable(x, y - 1, z) && this.walkable(x, y - 1, z)) {
      out.push([x, y - 1, z]);
    }
    return out;
  }
  // A* from a to b (cell coords). Returns [[x,y,z],...] or null.
  findPath(ax, ay, az, bx, by, bz, maxExpand = 26000) {
    if (!this.walkable(bx, by, bz)) {
      // nudge target to a nearby walkable cell
      let found = false;
      outer: for (let r = 1; r <= 3 && !found; r++)
        for (let dx = -r; dx <= r && !found; dx++)
          for (let dz = -r; dz <= r && !found; dz++)
            for (let dy = -1; dy <= 1; dy++)
              if (this.walkable(bx + dx, by + dy, bz + dz)) { bx += dx; by += dy; bz += dz; found = true; break outer; }
      if (!found) return null;
    }
    const open = new MinHeap();
    const came = new Map(), gScore = new Map();
    const startK = this.key(ax, ay, az), goalK = this.key(bx, by, bz);
    gScore.set(startK, 0);
    open.push(0, [ax, ay, az]);
    const nb = [];
    let expanded = 0;
    while (open.size) {
      const [cx, cy, cz] = open.pop();
      const ck = this.key(cx, cy, cz);
      if (ck === goalK) {
        const path = [[cx, cy, cz]];
        let k = ck;
        while (came.has(k)) { const p = came.get(k); path.push(p); k = this.key(p[0], p[1], p[2]); }
        return path.reverse();
      }
      if (++expanded > maxExpand) return null;
      const cg = gScore.get(ck);
      this.neighbors(cx, cy, cz, nb);
      for (const [nx, ny, nz] of nb) {
        const nk = this.key(nx, ny, nz);
        const ng = cg + 1 + Math.abs(ny - cy) * 1.5;
        if (ng < (gScore.get(nk) ?? Infinity)) {
          gScore.set(nk, ng);
          came.set(nk, [cx, cy, cz]);
          const h = Math.abs(bx - nx) + Math.abs(by - ny) * 2 + Math.abs(bz - nz);
          open.push(ng + h, [nx, ny, nz]);
        }
      }
    }
    return null;
  }
}

class MinHeap {
  constructor() { this.k = []; this.v = []; }
  get size() { return this.k.length; }
  push(key, val) {
    let i = this.k.length;
    this.k.push(key); this.v.push(val);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.k[p] <= this.k[i]) break;
      [this.k[p], this.k[i]] = [this.k[i], this.k[p]];
      [this.v[p], this.v[i]] = [this.v[i], this.v[p]];
      i = p;
    }
  }
  pop() {
    const top = this.v[0];
    const lk = this.k.pop(), lv = this.v.pop();
    if (this.k.length) {
      this.k[0] = lk; this.v[0] = lv;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1, r = l + 1;
        let m = i;
        if (l < this.k.length && this.k[l] < this.k[m]) m = l;
        if (r < this.k.length && this.k[r] < this.k[m]) m = r;
        if (m === i) break;
        [this.k[m], this.k[i]] = [this.k[i], this.k[m]];
        [this.v[m], this.v[i]] = [this.v[i], this.v[m]];
        i = m;
      }
    }
    return top;
  }
}

// ---------------------------------------------------------------------------
// a sailor's blocky body
const COLORS = {
  dungaree: 0x33506e, chambray: 0x6e8fae, skin: 0xd9a679, skin2: 0xb98a63, skin3: 0x8a6242,
  hat: 0xe8e6da, khaki: 0x9a8a62, denim2: 0x2a4056,
};
function makeSailor(officer) {
  const grp = new THREE.Group();
  const mat = (c) => new THREE.MeshLambertMaterial({ color: c });
  const skin = [COLORS.skin, COLORS.skin2, COLORS.skin3][(Math.random() * 3) | 0];
  const shirt = officer ? COLORS.khaki : (Math.random() < 0.5 ? COLORS.chambray : COLORS.dungaree);
  const pants = officer ? COLORS.khaki : COLORS.denim2;
  const box = (w, h, d, c) => new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(c));
  const torso = box(0.36, 0.62, 0.5, shirt); torso.position.y = 1.02;
  const head = box(0.34, 0.34, 0.34, skin); head.position.y = 1.52;
  const hat = box(0.38, 0.1, 0.38, officer ? COLORS.khaki : COLORS.hat); hat.position.y = 1.72;
  const legL = box(0.16, 0.72, 0.2, pants); legL.position.set(0, 0.36, -0.12);
  const legR = box(0.16, 0.72, 0.2, pants); legR.position.set(0, 0.36, 0.12);
  const armL = box(0.13, 0.55, 0.15, shirt); armL.position.set(0, 1.05, -0.32);
  const armR = box(0.13, 0.55, 0.15, shirt); armR.position.set(0, 1.05, 0.32);
  grp.add(torso, head, hat, legL, legR, armL, armR);
  return { grp, legL, legR, armL, armR };
}

// ---------------------------------------------------------------------------
export class Crew {
  constructor(G, count = 26) {
    this.G = G;
    this.nav = new Nav(G.ship.grid);
    this.sailors = [];
    this.parties = [
      { id: "TWO", members: [], task: null },
      { id: "FIVE", members: [], task: null },
    ];
    const hang = G.ship.hangouts;
    for (let i = 0; i < count; i++) {
      const officer = i < 3;
      const body = makeSailor(officer);
      const spot = hang[i % hang.length];
      const s = {
        body, officer,
        pos: spot.clone().add(new THREE.Vector3((Math.random() - 0.5), 0, (Math.random() - 0.5))),
        path: null, pathI: 0, idle: Math.random() * 8,
        speed: WALK_SPEED, phase: Math.random() * 7,
        party: null, working: false, yaw: Math.random() * 6.28,
      };
      body.grp.position.copy(s.pos);
      G.shipInner.add(body.grp);
      this.sailors.push(s);
      if (i >= count - 8) {
        const party = this.parties[i % 2];
        party.members.push(s);
        s.party = party;
      }
    }
    this.gq = false;
    this.mealtime = 0; // seconds left on a chow call — the crew drifts to the mess
    this._budget = 0;
  }

  soundGQ(on) {
    this.gq = on;
    for (const s of this.sailors) {
      s.idle = Math.random() * 2;
      s.speed = on ? RUN_SPEED : WALK_SPEED;
      if (on) s.path = null; // repath to stations
    }
  }

  // damage.js asks for a repair party at compartment c
  dispatchParty(c) {
    if (c.party) return;
    const free = this.parties.find((p) => !p.task);
    if (!free) { this.G.hud.log("ALL REPAIR PARTIES COMMITTED", "bad"); return; }
    free.task = c;
    c.party = { id: free.id, working: false };
    this.G.hud.log(`REPAIR ${free.id} LAID TO ${c.name}`);
    for (const s of free.members) { s.path = null; s.idle = 0; s.speed = RUN_SPEED; }
  }
  releaseParty(c) {
    const p = this.parties.find((pp) => pp.task === c);
    if (p) { p.task = null; for (const s of p.members) { s.path = null; s.idle = Math.random() * 4; s.working = false; s.speed = this.gq ? RUN_SPEED : WALK_SPEED; } }
    c.party = null;
  }

  destFor(s) {
    const G = this.G;
    if (s.party?.task) {
      const c = s.party.task.center;
      return new THREE.Vector3(c.x + (Math.random() - 0.5) * 3, s.party.task.room.walk, c.z + (Math.random() - 0.5) * 3);
    }
    const hang = G.ship.hangouts;
    // chow call: most of the crew heads for the mess-deck spots
    if (this.mealtime > 0 && !this.gq && Math.random() < 0.75) {
      const mess = hang.filter((h) => h.y === 11 && h.x >= 100 && h.x <= 142 && h.z >= 5 && h.z <= 28);
      if (mess.length) return mess[(Math.random() * mess.length) | 0];
    }
    return hang[(Math.random() * hang.length) | 0];
  }

  update(dt) {
    const G = this.G;
    this.mealtime = Math.max(0, this.mealtime - dt);
    this._budget = 2; // path searches allowed this frame
    for (const s of this.sailors) this.updateSailor(s, dt);
    // party working state: members near task?
    for (const p of this.parties) {
      if (!p.task) continue;
      const c = p.task;
      let near = 0;
      for (const s of p.members) {
        if (Math.abs(s.pos.x - c.center.x) < 6 && Math.abs(s.pos.y - c.room.walk) < 3 && Math.abs(s.pos.z - c.center.z) < 6) near++;
      }
      if (c.party) c.party.working = near >= 2;
    }
  }

  updateSailor(s, dt) {
    const b = s.body;
    if (s.working) {
      // bob at the casualty
      s.phase += dt * 6;
      b.armL.rotation.z = Math.sin(s.phase) * 0.9;
      b.armR.rotation.z = -Math.sin(s.phase) * 0.9;
      return;
    }
    if (!s.path) {
      s.idle -= dt;
      // settle limbs
      b.legL.rotation.z = b.legR.rotation.z = 0;
      b.armL.rotation.z = b.armR.rotation.z = 0;
      if (s.idle <= 0 && this._budget > 0) {
        this._budget--;
        const from = [Math.floor(s.pos.x), Math.round(s.pos.y), Math.floor(s.pos.z)];
        const to = this.destFor(s);
        const path = this.nav.findPath(from[0], from[1], from[2], Math.floor(to.x), Math.round(to.y), Math.floor(to.z));
        if (path && path.length > 1) { s.path = path; s.pathI = 1; }
        else s.idle = 4 + Math.random() * 6;
      }
      return;
    }
    // follow path
    const node = s.path[s.pathI];
    const tgt = new THREE.Vector3(node[0] + 0.5, node[1], node[2] + 0.5);
    const d = tgt.clone().sub(s.pos);
    const vertical = Math.abs(d.y) > 0.4 && Math.abs(d.x) + Math.abs(d.z) < 0.8;
    const speed = vertical ? CLIMB_SPEED : s.speed;
    const dist = d.length();
    if (dist < 0.15) {
      s.pathI++;
      if (s.pathI >= s.path.length) {
        s.path = null;
        s.idle = s.party?.task ? 0.5 : 6 + Math.random() * 20;
        if (s.party?.task) { s.working = true; s.body.grp.rotation.y = Math.random() * 6.28; }
        return;
      }
    } else {
      d.normalize().multiplyScalar(Math.min(speed * dt, dist));
      s.pos.add(d);
      if (!vertical && (d.x || d.z)) {
        s.yaw = Math.atan2(-d.z, d.x); // body's +X faces the direction of travel
      }
      // walk cycle
      s.phase += dt * speed * 3;
      const sw = Math.sin(s.phase) * (vertical ? 0.5 : 0.6);
      b.legL.rotation.z = sw; b.legR.rotation.z = -sw;
      b.armL.rotation.z = -sw * 0.7; b.armR.rotation.z = sw * 0.7;
    }
    b.grp.position.copy(s.pos);
    b.grp.rotation.y = s.yaw;
  }
}
